/**
 * EoT Claim Builder — Delay Analysis.
 *
 * Runs the chosen method against the imported programmes and the confirmed
 * events. Everything on this tab is computed by the CPM engine.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Play, GitBranch, AlertTriangle } from 'lucide-react';
import ImpactGantt from './ImpactGantt';

const METHODS = [
  { key: 'TIA', label: 'Time Impact Analysis' },
  { key: 'WINDOWS', label: 'Windows' },
  { key: 'AS_PLANNED_VS_AS_BUILT', label: 'As-Planned vs As-Built' },
  { key: 'COLLAPSED_AS_BUILT', label: 'Collapsed As-Built' },
];

const Stat = ({ label, value, tone, testId }) => (
  <div className="rounded-lg border border-border p-3" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className={`text-lg font-semibold ${tone || 'text-foreground'}`}>{value}</p>
  </div>
);

const Th = ({ cols }) => (
  <thead className="text-muted-foreground uppercase text-[10px]">
    <tr>{cols.map(h => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr>
  </thead>
);

export default function DelayAnalysisTab({ claimId, methods = [], onRan }) {
  const [runs, setRuns] = useState([]);
  const [method, setMethod] = useState(methods[0] || 'TIA');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/eot/claims/${claimId}/analysis`);
    setRuns(data.runs || []);
  }, [claimId]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/eot/claims/${claimId}/analysis/run`, { method });
      toast.success(`${data.grossDelayDays} day(s) of delay measured across ${data.eventCount} event(s)`);
      await load();
      onRan?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not run the analysis'); }
    finally { setBusy(false); }
  };

  const body = (r) => {
    if (r.method === 'TIA') {
      return (
        <table className="w-full text-xs" data-testid="eot-tia-table">
          <Th cols={['Event', 'Party', 'Window', 'Event days', 'Impact days', 'Float used', 'Critical', 'Completion before → after', 'Programme impacted', 'Affected activities']} />
          <tbody>
            {r.rows.map(row => (
              <tr key={row.eventId} className="border-t border-border align-top" data-testid={`eot-tia-row-${row.eventId}`}>
                <td className="px-2 py-1.5 text-foreground max-w-[200px]">
                  <span className="font-mono text-[10px] mr-1">{row.eventId}</span>{row.title}
                  {row.assumption && <div className="text-[10px] text-amber-600 mt-0.5">{row.assumption}</div>}
                </td>
                <td className="px-2 py-1.5 text-[11px]">{(row.responsibleParty || '').toLowerCase()}</td>
                <td className="px-2 py-1.5 text-[10px] text-muted-foreground">{row.startDate}{row.endDate ? ` → ${row.endDate}` : ''}</td>
                <td className="px-2 py-1.5 text-center">{row.eventDays}</td>
                <td className="px-2 py-1.5 text-center font-semibold text-foreground">{row.impactDays}</td>
                <td className="px-2 py-1.5 text-center text-muted-foreground">{row.floatConsumedDays}</td>
                <td className="px-2 py-1.5 text-center">{row.critical ? 'yes' : 'no'}</td>
                <td className="px-2 py-1.5 text-[10px] text-muted-foreground">{row.completionBefore} → {row.completionAfter}</td>
                <td className="px-2 py-1.5 text-[10px] text-muted-foreground">{row.programme}{row.programmeDataDate ? ` · ${row.programmeDataDate}` : ''}</td>
                <td className="px-2 py-1.5 text-[10px] text-muted-foreground max-w-[160px]">
                  {(row.affected || []).map(a => a.name).join(', ') || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    if (r.method === 'WINDOWS') {
      return (
        <table className="w-full text-xs" data-testid="eot-windows-table">
          <Th cols={['Window', 'Completion moved', 'Slip (days)', 'Employer', 'Contractor', 'Unexplained', 'Concurrent', 'Events in the window']} />
          <tbody>
            {r.rows.map((row, i) => (
              <tr key={i} className="border-t border-border align-top" data-testid={`eot-window-row-${i}`}>
                <td className="px-2 py-1.5 text-foreground">{row.window}
                  <div className="text-[10px] text-muted-foreground">{row.fromProgramme} → {row.toProgramme}</div></td>
                <td className="px-2 py-1.5 text-[10px] text-muted-foreground">{row.completionFrom} → {row.completionTo}</td>
                <td className="px-2 py-1.5 text-center font-semibold">{row.slipDays}</td>
                <td className="px-2 py-1.5 text-center">{row.employerDays}</td>
                <td className="px-2 py-1.5 text-center">{row.contractorDays}</td>
                <td className="px-2 py-1.5 text-center text-amber-600">{row.unexplainedDays || 0}</td>
                <td className="px-2 py-1.5 text-center">{row.concurrent ? 'yes' : 'no'}</td>
                <td className="px-2 py-1.5 text-[10px] text-muted-foreground max-w-[260px]">
                  {(row.events || []).map(e => `${e.eventId} ${e.title}${e.critical ? ' (critical)' : ''}`).join(' · ') || 'no events recorded in this window'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    if (r.method === 'AS_PLANNED_VS_AS_BUILT') {
      return (
        <table className="w-full text-xs" data-testid="eot-apab-table">
          <Th cols={['Activity', 'Planned finish', 'Actual finish', 'Variance (days)', 'Added here', 'Critical', 'Attributed to', 'Events overlapping']} />
          <tbody>
            {r.rows.slice(0, 120).map(row => (
              <tr key={row.activityId} className="border-t border-border align-top" data-testid={`eot-apab-row-${row.activityId}`}>
                <td className="px-2 py-1.5 text-foreground max-w-[220px]">{row.activity}</td>
                <td className="px-2 py-1.5 text-[10px] text-muted-foreground">{row.plannedFinish}</td>
                <td className="px-2 py-1.5 text-[10px] text-muted-foreground">{row.actualFinish}</td>
                <td className="px-2 py-1.5 text-center font-semibold">{row.varianceDays}</td>
                <td className="px-2 py-1.5 text-center">{row.ownVarianceDays}</td>
                <td className="px-2 py-1.5 text-center">{row.critical ? 'yes' : 'no'}</td>
                <td className="px-2 py-1.5 text-[11px]">{(row.attributedTo || '').toLowerCase()}</td>
                <td className="px-2 py-1.5 text-[10px] text-muted-foreground max-w-[200px]">
                  {(row.events || []).map(e => e.eventId).join(', ') || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    return (
      <table className="w-full text-xs" data-testid="eot-cab-table">
        <Th cols={['Employer event', 'Event days', 'Activities collapsed', 'Completion pulls back by', 'As-built → but-for']} />
        <tbody>
          {r.rows.map(row => (
            <tr key={row.eventId} className="border-t border-border align-top" data-testid={`eot-cab-row-${row.eventId}`}>
              <td className="px-2 py-1.5 text-foreground max-w-[240px]">
                <span className="font-mono text-[10px] mr-1">{row.eventId}</span>{row.title}
                {row.note && <div className="text-[10px] text-amber-600 mt-0.5">{row.note}</div>}
              </td>
              <td className="px-2 py-1.5 text-center">{row.eventDays}</td>
              <td className="px-2 py-1.5 text-center">{row.activities}</td>
              <td className="px-2 py-1.5 text-center font-semibold">{row.collapseDays}</td>
              <td className="px-2 py-1.5 text-[10px] text-muted-foreground">{row.completionBefore} → {row.completionAfter}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="space-y-4 pt-4" data-testid="eot-analysis-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm text-foreground font-medium">Delay analysis</p>
          <p className="text-[11px] text-muted-foreground">
            Runs against the programmes in Schedule Health and the confirmed events. Each run replaces the previous run of the same method.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {METHODS.map(m => (
            <button key={m.key} type="button" onClick={() => setMethod(m.key)} data-testid={`eot-method-${m.key}`}
                    className={`px-2.5 py-1.5 rounded-md border text-[11px] transition-colors ${method === m.key
                      ? 'border-[#6B5CE7] bg-[#6B5CE7]/10 text-foreground'
                      : 'border-border text-muted-foreground hover:border-[#6B5CE7]/50'}`}>
              {m.label}{methods.includes(m.key) ? ' ★' : ''}
            </button>
          ))}
          <Button size="sm" onClick={run} disabled={busy} data-testid="eot-run-analysis"
                  className="bg-gradient-to-r from-[#1E4E8C] to-[#6B5CE7]">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
            Run
          </Button>
        </div>
      </div>

      {runs.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="eot-no-runs">
          Nothing run yet. Import the programmes in Schedule Health, confirm the events, then run a method here.
        </p>
      ) : runs.map(r => (
        <div key={r.id} className="rounded-lg border border-border p-3 space-y-3" data-testid={`eot-run-${r.method}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-foreground font-medium flex items-center gap-2">
              <GitBranch className="w-3.5 h-3.5 text-[#6B5CE7]" />
              {(METHODS.find(m => m.key === r.method) || {}).label}
              <Badge variant="outline" className="text-[9px]">{r.eventCount} event(s)</Badge>
              {r.gateAtRun && r.gateAtRun !== 'PASS' && (
                <Badge variant="outline" className="text-[9px] text-amber-600 border-amber-600/40">
                  programme quality {r.gateAtRun.toLowerCase()}
                </Badge>
              )}
            </p>
            <p className="text-[10px] text-muted-foreground">Run {String(r.runAt).slice(0, 16).replace('T', ' ')} by {r.runBy}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <Stat label="Gross delay" value={`${r.grossDelayDays} d`} testId={`eot-run-gross-${r.method}`} />
            <Stat label="Employer-caused" value={`${r.employerDelayDays} d`} tone="text-emerald-600" testId={`eot-run-employer-${r.method}`} />
            <Stat label="Contractor-caused" value={`${r.contractorDelayDays} d`} tone="text-red-500" testId={`eot-run-contractor-${r.method}`} />
            {r.unexplainedDelayDays ? (
              <Stat label="Unexplained" value={`${r.unexplainedDelayDays} d`} tone="text-amber-500"
                    testId={`eot-run-unexplained-${r.method}`} />
            ) : null}
            <Stat label={r.method === 'COLLAPSED_AS_BUILT' ? 'But-for completion' : 'Completion before'}
                  value={r.baseCompletion || '—'} />
            <Stat label={r.method === 'COLLAPSED_AS_BUILT' ? 'As-built completion' : 'Completion after'}
                  value={r.finalCompletion || '—'} />
          </div>

          <div className="overflow-x-auto">{body(r)}</div>

          {['TIA', 'COLLAPSED_AS_BUILT'].includes(r.method) && (
            <ImpactGantt claimId={claimId} method={r.method} />
          )}

          {(r.warnings || []).map((w, i) => (
            <p key={i} className="text-[11px] text-amber-600 flex items-start gap-1.5" data-testid={`eot-run-warning-${r.method}-${i}`}>
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{w}
            </p>
          ))}

          {r.concurrency?.length > 0 && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2" data-testid={`eot-concurrency-${r.method}`}>
              <p className="text-[11px] text-foreground font-medium flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                Concurrent delay detected — {r.concurrencyRule?.toLowerCase()} applies in Entitlement
              </p>
              {r.concurrency.slice(0, 8).map((c, i) => (
                <p key={i} className="text-[10px] text-muted-foreground mt-0.5">
                  {c.employerEvent} and {c.contractorEvent} overlap {c.from} → {c.to} ({c.overlapDays} day(s))
                </p>
              ))}
            </div>
          )}

          <p className="text-[10px] text-muted-foreground">
            Programmes used: {(r.programmesUsed || []).map(p => `${p.label}${p.dataDate ? ` (${p.dataDate})` : ''}`).join(' · ')}
          </p>
        </div>
      ))}
    </div>
  );
}
